import { useCallback, useEffect, useMemo, useRef } from "react";
import useQueryState from "shared/hooks/useQueryState";
import { ModerationType } from "shared/pages/teams/const/ModerationTypes";
import { AppTester } from "supporting";
import { useRouteMatch } from "react-router-dom";
import { examTabs } from "../const/misc";
import { useProfile } from "@brg/auth";
import { bProfile } from "core/utils/bProfile";
import { useDispatch } from "react-redux";
import { useIsMobile } from "@brg/ui";
import { useEncodedStateFilter } from "shared/components/Filters";

const deduplicate = (array: any[]) => {
    return Array.from(new Set(array))
  }
  

const useTeamTabs = (team: any) => {
    const profile = useProfile();
    const {
        tabs: tabsRawInitial,
        categories,
        isLargeTabs,
        initialTab,
        mainFilter
    } = team;

    const isLectureMedia = AppTester.isLectureMediiia();
    const isBrnd = AppTester.isBrnd() && (team?.contextName === 'brnd' || !team?.contextName);
    const isMedia = AppTester.isMediiia() && !isLectureMedia;
    const isExamOtherTeams= useRouteMatch(['/examnews', '/entranceexams', '/teams/efda83e286c443db8aa65135c911f1cf']);
    const isExam = AppTester.isExamHSE() && !isExamOtherTeams;

    const isMain = !!useRouteMatch(['/'])?.isExact;
    const isForcedLanding = !!useRouteMatch(['/brnd', '/teams/:teamName/brnd'])?.isExact;
    const isMainPage = isMain || isForcedLanding;

    const isModer = useMemo(() => profile && (bProfile.isModer(profile) || bProfile.isMiddleModer(profile)), [profile]);

    const isMobile = useIsMobile();

    const isCourses = team?.moderationType === ModerationType.LMS;

    const [{ tab, category }, setQueryParams] = useQueryState();
    const { sorting, setSorting } = useEncodedStateFilter()

    const convertChannelFilter = useCallback(() => {
        const mainParams = {
            projectFilter: null,
            sort: null,
            statuses: [0, 3],  
        }

        return mainParams
    } , [sorting])

    console.log('tst qwe', category)

    const setCurrentTab = (value: string) => {
        if (isLectureMedia && isMobile) {
            setSorting(1)
        } else if (isLectureMedia) {
            setSorting(1)
            setQueryParams({ tab: value }, { replace: true })
        } else {
            setQueryParams({ tab: value, ...([50,51,52]?.includes(Number(sorting)) ? { sorting: 1} : {}) }, { replace: true })
        }
    }

    const hasCategories = !!categories?.length;

    const defaultCategory = useMemo(() => {
        if (!categories?.length) {
            return 'main';
        } else {
            const dc = categories.find((x: any) => x.isMain);

            if (!dc) {
                return 'main';
            } else {
                return dc.path;
            }
        }
    }, [categories]);

    const currentCategory = !!category ? category : defaultCategory;

    const tabsRaw = useMemo(() => {

        if (isExam && isModer) {
            return [...tabsRawInitial, ...examTabs]
        }

        return tabsRawInitial?.map((tab: any) => {
            if (tab.filter?.moderationTypes?.includes(ModerationType.ChannelGroupOrCourse)) {
                return {
                    ...tab,
                    withoutEmptyParams: true,
                    filter: {
                        ...tab?.filter,
                        ...convertChannelFilter()
                    }
                }
            }
            return tab
        })
    }, [isExam, tabsRawInitial, isModer, sorting, isCourses])

    const tabs = useMemo(() => {
        if (!!categories?.length) {
            return tabsRaw.filter(({ category }: any) => category === currentCategory)
        }
        return tabsRaw;
    }, [tabsRaw, currentCategory])

    const tabColors = useMemo(() => {
        return tabs?.reduce((acc: any, { color, path }: any) => {
            if (color) {
                return {
                    [path]: color,
                }
            }
            return acc;
        }, {})
    }, [tabs]);

    const contextTabs = useMemo(() => {
        return tabs.reduce((res: any, { path }: any) => ({
            ...res,
            [path]: path,
        }), {})
    }, [tabs])

    const defaultTab = useMemo(() => {
        if (!tabs.length) {
            return '';
        } else {
            const dt = tabs.find((x: any) => {
                if (hasCategories) {
                    return x.category === currentCategory && x.isMain
                }
                return x.isMain
            });

            if (!dt) {
                return tabs?.[0]?.path || '';
            } else {
                return dt.path;
            }
        }
    }, [tabs]);

    const tabIndex = useMemo(() => {
        return tabs.map(({ path, filter }: any) => ({
            [path]: deduplicate([
                ...(filter.tagIds || []), 
                ...(filter.requiredTagIds || []), 
                ...(mainFilter?.tagIds || []), 
                ...(mainFilter?.requiredTagIds || [])]
            ),
        }), {})
    }, [tabs, mainFilter])

    const currentTab = !!tab ? tab : defaultTab;

    const titleIsTabs = (isLargeTabs || hasCategories) && !isBrnd && !(isMedia && isMainPage);

    const categoryList = useMemo(() => {
        return categories?.reduce((res: any, {
            name,
            path,
            href,
            isOpenInNewTab
        }: any) => {
            return {
                ...res,
                [path]: href ? {
                    action: () => {
                        window.open(href, isOpenInNewTab ? '_blank' : '_self');
                    },
                    element: () => name
                } : name,
            }
        }, {})
    }, [tabs, currentTab]);

    const tabList = useMemo(() => {
        return tabs?.reduce((res: any, {
            name,
            path,
            href,
            isOpenInNewTab
        }: any) => {
            return {
                ...res,
                [path]: href || isOpenInNewTab ? {
                    action: () => {
                        window.open(href || `${window.location.pathname}?tab=${path}`, isOpenInNewTab ? '_blank' : '_self');
                    },
                    element: () => name
                } : name,
            }
        }, {})
    }, [tabs, currentTab]);

    useEffect(() => {
        if (!currentTab) {
            setCurrentTab(initialTab)
        }
    }, [initialTab, currentTab])

    useEffect(() => {
        if (tab) {
            setCurrentTab(tab);
        }
    }, [tab]);

    return {
        tabs,
        tabsRaw,
        tabList,
        contextTabs,
        categoryList,
        currentTab,
        currentCategory,
        titleIsTabs,
        tabColors,
        tabIndex,
        setCurrentTab,
    }
}

export default useTeamTabs;import { useCallback, useEffect, useMemo, useRef } from "react";
import useQueryState from "shared/hooks/useQueryState";
import { ModerationType } from "shared/pages/teams/const/ModerationTypes";
import { AppTester } from "supporting";
import { useRouteMatch } from "react-router-dom";
import { examTabs } from "../const/misc";
import { useProfile } from "@brg/auth";
import { bProfile } from "core/utils/bProfile";
import { useDispatch } from "react-redux";
import { useIsMobile } from "@brg/ui";
import { useEncodedStateFilter } from "shared/components/Filters";

const deduplicate = (array: any[]) => {
    return Array.from(new Set(array))
  }
  

const useTeamTabs = (team: any) => {
    const profile = useProfile();
    const {
        tabs: tabsRawInitial,
        categories,
        isLargeTabs,
        initialTab,
        mainFilter
    } = team;

    const isLectureMedia = AppTester.isLectureMediiia();
    const isBrnd = AppTester.isBrnd() && (team?.contextName === 'brnd' || !team?.contextName);
    const isMedia = AppTester.isMediiia() && !isLectureMedia;
    const isExamOtherTeams= useRouteMatch(['/examnews', '/entranceexams', '/teams/efda83e286c443db8aa65135c911f1cf']);
    const isExam = AppTester.isExamHSE() && !isExamOtherTeams;

    const isMain = !!useRouteMatch(['/'])?.isExact;
    const isForcedLanding = !!useRouteMatch(['/brnd', '/teams/:teamName/brnd'])?.isExact;
    const isMainPage = isMain || isForcedLanding;

    const isModer = useMemo(() => profile && (bProfile.isModer(profile) || bProfile.isMiddleModer(profile)), [profile]);

    const isMobile = useIsMobile();

    const isCourses = team?.moderationType === ModerationType.LMS;

    const [{ tab, category }, setQueryParams] = useQueryState();
    const { sorting, setSorting } = useEncodedStateFilter()

    const convertChannelFilter = useCallback(() => {
        const mainParams = {
            projectFilter: null,
            sort: null,
            statuses: [0, 3],  
        }

        return mainParams
    } , [sorting])

    console.log('tst qwe', category)

    const setCurrentTab = (value: string) => {
        if (isLectureMedia && isMobile) {
            setSorting(1)
        } else if (isLectureMedia) {
            setSorting(1)
            setQueryParams({ tab: value }, { replace: true })
        } else {
            setQueryParams({ tab: value, ...([50,51,52]?.includes(Number(sorting)) ? { sorting: 1} : {}) }, { replace: true })
        }
    }

    const hasCategories = !!categories?.length;

    const defaultCategory = useMemo(() => {
        if (!categories?.length) {
            return 'main';
        } else {
            const dc = categories.find((x: any) => x.isMain);

            if (!dc) {
                return 'main';
            } else {
                return dc.path;
            }
        }
    }, [categories]);

    const currentCategory = !!category ? category : defaultCategory;

    const tabsRaw = useMemo(() => {

        if (isExam && isModer) {
            return [...tabsRawInitial, ...examTabs]
        }

        return tabsRawInitial?.map((tab: any) => {
            if (tab.filter?.moderationTypes?.includes(ModerationType.ChannelGroupOrCourse)) {
                return {
                    ...tab,
                    withoutEmptyParams: true,
                    filter: {
                        ...tab?.filter,
                        ...convertChannelFilter()
                    }
                }
            }
            return tab
        })
    }, [isExam, tabsRawInitial, isModer, sorting, isCourses])

    const tabs = useMemo(() => {
        if (!!categories?.length) {
            return tabsRaw.filter(({ category }: any) => category === currentCategory)
        }
        return tabsRaw;
    }, [tabsRaw, currentCategory])

    const tabColors = useMemo(() => {
        return tabs?.reduce((acc: any, { color, path }: any) => {
            if (color) {
                return {
                    [path]: color,
                }
            }
            return acc;
        }, {})
    }, [tabs]);

    const contextTabs = useMemo(() => {
        return tabs.reduce((res: any, { path }: any) => ({
            ...res,
            [path]: path,
        }), {})
    }, [tabs])

    const defaultTab = useMemo(() => {
        if (!tabs.length) {
            return '';
        } else {
            const dt = tabs.find((x: any) => {
                if (hasCategories) {
                    return x.category === currentCategory && x.isMain
                }
                return x.isMain
            });

            if (!dt) {
                return tabs?.[0]?.path || '';
            } else {
                return dt.path;
            }
        }
    }, [tabs]);

    const tabIndex = useMemo(() => {
        return tabs.map(({ path, filter }: any) => ({
            [path]: deduplicate([
                ...(filter.tagIds || []), 
                ...(filter.requiredTagIds || []), 
                ...(mainFilter?.tagIds || []), 
                ...(mainFilter?.requiredTagIds || [])]
            ),
        }), {})
    }, [tabs, mainFilter])

    const currentTab = !!tab ? tab : defaultTab;

    const titleIsTabs = (isLargeTabs || hasCategories) && !isBrnd && !(isMedia && isMainPage);

    const categoryList = useMemo(() => {
        return categories?.reduce((res: any, {
            name,
            path,
            href,
            isOpenInNewTab
        }: any) => {
            return {
                ...res,
                [path]: href ? {
                    action: () => {
                        window.open(href, isOpenInNewTab ? '_blank' : '_self');
                    },
                    element: () => name
                } : name,
            }
        }, {})
    }, [tabs, currentTab]);

    const tabList = useMemo(() => {
        return tabs?.reduce((res: any, {
            name,
            path,
            href,
            isOpenInNewTab
        }: any) => {
            return {
                ...res,
                [path]: href || isOpenInNewTab ? {
                    action: () => {
                        window.open(href || `${window.location.pathname}?tab=${path}`, isOpenInNewTab ? '_blank' : '_self');
                    },
                    element: () => name
                } : name,
            }
        }, {})
    }, [tabs, currentTab]);

    useEffect(() => {
        if (!currentTab) {
            setCurrentTab(initialTab)
        }
    }, [initialTab, currentTab])

    useEffect(() => {
        if (tab) {
            setCurrentTab(tab);
        }
    }, [tab]);

    return {
        tabs,
        tabsRaw,
        tabList,
        contextTabs,
        categoryList,
        currentTab,
        currentCategory,
        titleIsTabs,
        tabColors,
        tabIndex,
        setCurrentTab,
    }
}

export default useTeamTabs;